import { z } from "zod";
import { AssessedLocationSourceV1, KnownLocationV1, LocationAssessmentV1 } from "./assessments.ts";
import { Decision, IsoDateTime, OperatorEnvironment, PlayerRef } from "./common.ts";
import { GeoTrustLocationOption } from "./interaction.ts";
import { PreciseLocationV1 } from "./ports.ts";
import { LocationState } from "./verify.ts";

// Location Intelligence facade DTOs v1 (change split-core-into-trust-contexts, design D2 and D11):
// what the Decision Engine sends to and receives from `LocationFacade` besides the
// `LocationAssessmentV1` it gets back (assessments.ts). In process today, serializable and strict
// (ADR 0001 R5). The precise coordinates cross this boundary only towards Location, which seals
// them (envelope encryption) and is the only module that keeps them; they are never logged,
// published or stored in clear.

/**
 * The consented browser position of one Interaction, as the SDK collected it. Sensitive: only the
 * Decision Engine hands it to Location (`assess`, `sealPrecise`); nothing else carries it.
 */
export const PreciseCoordinatesV1 = z
  .object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    accuracy_m: z.number().nonnegative(),
    collected_at: IsoDateTime,
  })
  .strict()
  .meta({ id: "PreciseCoordinatesV1" });
export type PreciseCoordinatesV1 = z.infer<typeof PreciseCoordinatesV1>;

/**
 * Decision Engine → Location (`LocationFacade.assess`): the client IP from the trusted gateway
 * header (never from the body), the browser's location permission and, when granted and given,
 * its position; `at` is the Interaction time (the known location's time).
 */
export const LocationAssessmentRequestV1 = z
  .object({
    source_ip: z.string().min(1).max(64).optional(),
    location_permission: LocationState,
    precise_location: PreciseCoordinatesV1.optional(),
    /**
     * The Interaction's GeoTrust `location` option (design D3): `high_accuracy` requires the
     * precise position and `none` accepts none (Location refuses the request otherwise). Absent →
     * no requirement (the position is used when given).
     */
    location_option: GeoTrustLocationOption.optional(),
    at: IsoDateTime,
  })
  .strict()
  .meta({ id: "LocationAssessmentRequestV1" });
export type LocationAssessmentRequestV1 = z.infer<typeof LocationAssessmentRequestV1>;

/**
 * Decision Engine → Location (`LocationFacade.sealPrecise`), once the Interaction is decided and
 * before it is persisted: Location seals and keeps the precise coordinates (when given), and keeps
 * the assessed sources and, for an `allow` with a known location, the player's known location
 * (the history `lastKnownLocation` reads). `decided_at` is the Interaction's completion time.
 */
export const SealLocationV1 = z
  .object({
    verification_id: z.uuid(),
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    player_ref: PlayerRef,
    decided_at: IsoDateTime,
    decision: Decision,
    /** Absent when no assessment was made (location resolution not wired in the role). */
    assessment: LocationAssessmentV1.optional(),
    precise_location: PreciseCoordinatesV1.optional(),
  })
  .strict()
  .meta({ id: "SealLocationV1" });
export type SealLocationV1 = z.infer<typeof SealLocationV1>;

/**
 * Location → Decision Engine: `sealed_ref` is the opaque reference of the sealed coordinates the
 * Decision Engine keeps with the Interaction (never the coordinates or their ciphertext); absent
 * when no precise coordinates were given.
 */
export const SealedLocationV1 = z
  .object({ sealed_ref: z.string().min(1).max(128).optional() })
  .strict()
  .meta({ id: "SealedLocationV1" });
export type SealedLocationV1 = z.infer<typeof SealedLocationV1>;

/**
 * Decision Engine → Location (`LocationFacade.lastKnownLocation`): the player's most recent
 * allowed Interaction with a known location in the operator environment (`travel_speed` input).
 * Operator and environment come from the verified session token, never from request input.
 */
export const KnownLocationQueryV1 = z
  .object({
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    player_ref: PlayerRef,
  })
  .strict()
  .meta({ id: "KnownLocationQueryV1" });
export type KnownLocationQueryV1 = z.infer<typeof KnownLocationQueryV1>;

/** Location → Decision Engine: the known location, or none (no previous allowed position). */
export const LastKnownLocationV1 = z
  .object({ known_location: KnownLocationV1.optional() })
  .strict()
  .meta({ id: "LastKnownLocationV1" });
export type LastKnownLocationV1 = z.infer<typeof LastKnownLocationV1>;

/** Most Interactions one `RecordedLocationSourcesQueryV1` may name (one replay batch). */
export const MAX_RECORDED_LOCATION_SOURCES_QUERY = 5000;

/**
 * Decision Engine → Location (`LocationFacade.recordedSources`; split-core-into-trust-contexts
 * task 8.1, design D11): the evaluated sources Location recorded for a batch of decided
 * Interactions of the installation's environment, named by id and decision time (the partition
 * key), for the replay of `DecisionMade` (core no longer records its own location sources).
 */
export const RecordedLocationSourcesQueryV1 = z
  .object({
    environment: OperatorEnvironment,
    verifications: z
      .array(z.object({ verification_id: z.uuid(), decided_at: IsoDateTime }).strict())
      .min(1)
      .max(MAX_RECORDED_LOCATION_SOURCES_QUERY),
  })
  .strict()
  .meta({ id: "RecordedLocationSourcesQueryV1" });
export type RecordedLocationSourcesQueryV1 = z.infer<typeof RecordedLocationSourcesQueryV1>;

/**
 * Location → Decision Engine: the recorded sources (coarse only, in the order `ip`, `browser`,
 * `wifi`) of each named Interaction Location holds a record of; an Interaction without a record
 * (decided before Location kept records) is absent.
 */
export const RecordedLocationSourcesV1 = z
  .object({
    items: z.array(
      z.object({ verification_id: z.uuid(), sources: z.array(AssessedLocationSourceV1) }).strict(),
    ),
  })
  .strict()
  .meta({ id: "RecordedLocationSourcesV1" });
export type RecordedLocationSourcesV1 = z.infer<typeof RecordedLocationSourcesV1>;

/**
 * Location → Explorer (`PreciseLocationReader`, `LocationFacade.readPrecise`): the coordinates of
 * each Interaction that still has them, decrypted in process, and the ids whose coordinates were
 * purged by retention (shown as an approximate location). Never cached, stored or logged.
 */
export const PreciseLocationsV1 = z
  .object({
    locations: z.array(PreciseLocationV1),
    expired: z.array(z.uuid()),
  })
  .strict()
  .meta({ id: "PreciseLocationsV1" });
export type PreciseLocationsV1 = z.infer<typeof PreciseLocationsV1>;
